<template>
  <div
    v-if="isOpen"
    class="audit-sidebar-wrapper"
    :class="{ 'audit-sidebar-wrapper--overlay': overlay }">
    <div class="audit-sidebar">
    <!-- Header -->
    <div class="sidebar-header">
      <div class="header-content">
        <v-icon size="20" class="header-icon">mdi-history</v-icon>
        <div class="header-text">
          <h2 class="header-title">Audit History</h2>
          <span class="header-subtitle">{{ headerSubtitle }}</span>
        </div>
      </div>
      <div class="header-actions">
        <v-btn icon x-small class="close-btn" @click="closeSidebar">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Mode Toggle (hidden when hideModeToggle prop is true) -->
    <div v-if="!hideModeToggle" class="mode-toggle-container">
      <v-btn-toggle
        :value="mode"
        @change="onModeChange"
        mandatory
        dense
        class="mode-toggle">
        <v-btn value="register" small class="mode-btn">
          Register
        </v-btn>
        <v-btn value="row" small class="mode-btn" :disabled="!selectedRow">
          Row
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- Filters -->
    <div class="filters-section">
      <div class="filters-header" @click="showFilters = !showFilters">
        <div class="filters-title">
          <v-icon size="16">mdi-filter-variant</v-icon>
          <span>Filters</span>
          <v-chip
            v-if="activeFiltersCount > 0"
            x-small
            color="primary"
            class="filter-count">
            {{ activeFiltersCount }}
          </v-chip>
        </div>
        <v-icon size="16" :class="{ 'rotated': showFilters }">
          mdi-chevron-down
        </v-icon>
      </div>

      <transition name="slide">
        <div v-if="showFilters" class="filters-body">
          <!-- Action Type Filter -->
          <v-select
            v-model="localFilters.actionType"
            :items="actionTypeOptions"
            label="Action Type"
            dense
            outlined
            hide-details
            clearable
            class="filter-input"
            @change="applyFilters" />

          <!-- Date Range -->
          <div class="date-range">
            <v-menu
              v-model="fromDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="localFilters.fromDate"
                  label="From"
                  dense
                  outlined
                  hide-details
                  readonly
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  class="date-input"
                  @click:clear="localFilters.fromDate = null; applyFilters()" />
              </template>
              <v-date-picker
                v-model="localFilters.fromDate"
                @input="fromDateMenu = false; applyFilters()"
                no-title
                scrollable />
            </v-menu>

            <v-menu
              v-model="toDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="localFilters.toDate"
                  label="To"
                  dense
                  outlined
                  hide-details
                  readonly
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  class="date-input"
                  @click:clear="localFilters.toDate = null; applyFilters()" />
              </template>
              <v-date-picker
                v-model="localFilters.toDate"
                @input="toDateMenu = false; applyFilters()"
                no-title
                scrollable />
            </v-menu>
          </div>

          <!-- Clear Filters -->
          <v-btn
            v-if="activeFiltersCount > 0"
            text
            x-small
            color="primary"
            class="clear-filters-btn"
            @click="clearFilters">
            <v-icon size="14" left>mdi-close</v-icon>
            Clear Filters
          </v-btn>
        </div>
      </transition>
    </div>

    <!-- Toolbar -->
    <div class="sidebar-toolbar">
      <div class="results-info">
        <span class="results-count">{{ total }}</span>
        <span class="results-label">{{ total === 1 ? 'Entry' : 'Entries' }}</span>
      </div>
      <div class="toolbar-actions">
        <v-btn icon small @click="refresh" :loading="loading" class="refresh-btn">
          <v-icon size="18">mdi-refresh</v-icon>
        </v-btn>
        <v-btn
          v-if="canClearLogs"
          icon
          small
          class="clear-logs-btn"
          @click="showClearDialog = true">
          <v-icon size="18">mdi-delete-sweep</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Content -->
    <div class="sidebar-content">
      <!-- Loading State -->
      <div v-if="loading && logs.length === 0" class="loading-state">
        <v-progress-circular indeterminate color="primary" size="32" />
        <span>Loading audit history...</span>
      </div>

      <!-- Empty State -->
      <div v-else-if="!loading && logs.length === 0" class="empty-state">
        <div class="empty-icon">
          <v-icon size="48" color="grey">mdi-history</v-icon>
        </div>
        <h3 class="empty-title">No History Yet</h3>
        <p class="empty-subtitle">
          {{ mode === 'row' ? 'No changes have been recorded for this row.' : 'No audit entries found for this register.' }}
        </p>
      </div>

      <!-- Audit Log List -->
      <div v-else class="audit-list">
        <AuditLogItem
          v-for="(log, index) in logs"
          :key="log.id"
          :log="log"
          :is-last="index === logs.length - 1" />

        <!-- Load More -->
        <div v-if="hasMore" class="load-more">
          <v-btn
            text
            small
            color="primary"
            :loading="loading"
            @click="loadMore">
            Load More
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Clear Logs Confirmation Dialog -->
    <v-dialog v-model="showClearDialog" max-width="400">
      <v-card class="clear-dialog">
        <v-card-title class="clear-dialog-title">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          Clear Audit Logs
        </v-card-title>
        <v-card-text class="clear-dialog-text">
          <p>Are you sure you want to clear the audit logs for this register?</p>
          <p class="warning-text">This action cannot be undone.</p>

          <v-checkbox
            v-model="clearOlderOnly"
            hide-details
            class="mt-4">
            <template #label>
              <div class="d-flex flex-column">
                <span>Only clear logs older than 30 days</span>
                <span class="text-caption text--secondary">
                  {{ clearOlderOnly ? 'Recent logs will be preserved' : `Will clear all ${total} log${total !== 1 ? 's' : ''}` }}
                </span>
              </div>
            </template>
          </v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showClearDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            depressed
            :loading="clearing"
            @click="confirmClearLogs">
            Clear Logs
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AuditLogItem from './AuditLogItem.vue';
import permissions from '@Share/config/permissions';
import { $has } from '@/utils';
import { log, warn, error } from '@/utils/debugLogger';

export default {
  name: 'RegisterAuditSidebar',
  components: {
    AuditLogItem
  },
  props: {
    registerId: {
      type: String,
      default: null
    },
    registerName: {
      type: String,
      default: ''
    },
    /** Hide the Register/Row mode toggle (useful when only register mode is relevant) */
    hideModeToggle: {
      type: Boolean,
      default: false
    },
    /**
     * Render as an absolutely-positioned overlay that floats over sibling content
     * instead of an in-flow "push" sidebar. Opt-in: used by the register data view
     * where the data table + 3D viewer already consume the row, so pushing a 420px
     * sidebar would overflow the viewport. Requires a `position: relative` ancestor.
     */
    overlay: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showFilters: false,
      fromDateMenu: false,
      toDateMenu: false,
      localFilters: {
        actionType: null,
        fromDate: null,
        toDate: null
      },
      showClearDialog: false,
      clearOlderOnly: false, // Default to clear ALL logs (not just old ones)
      clearing: false,
      actionTypeOptions: [
        { text: 'All Actions', value: null },
        { text: 'Create', value: 'Create' },
        { text: 'Update', value: 'Update' },
        { text: 'Delete', value: 'Delete' },
        { text: 'Import', value: 'Import' },
        { text: 'Export', value: 'Export' },
        { text: 'Schema Change', value: 'SchemaChange' },
        { text: 'User Assignment', value: 'UserAssignment' }
      ]
    };
  },
  computed: {
    ...mapGetters('REGISTERS', [
      'GET_AUDIT_LOGS',
      'GET_AUDIT_LOGS_TOTAL',
      'GET_AUDIT_LOGS_LOADING',
      'GET_AUDIT_SIDEBAR_OPEN',
      'GET_AUDIT_SIDEBAR_MODE',
      'GET_SELECTED_ROW_FOR_AUDIT',
      'GET_AUDIT_FILTERS',
      'GET_AUDIT_PAGINATION'
    ]),
    isOpen() {
      return this.GET_AUDIT_SIDEBAR_OPEN;
    },
    mode() {
      return this.GET_AUDIT_SIDEBAR_MODE;
    },
    selectedRow() {
      return this.GET_SELECTED_ROW_FOR_AUDIT;
    },
    logs() {
      return this.GET_AUDIT_LOGS || [];
    },
    total() {
      return this.GET_AUDIT_LOGS_TOTAL || 0;
    },
    loading() {
      return this.GET_AUDIT_LOGS_LOADING;
    },
    canClearLogs() {
      // Only users with delete permission can clear audit logs
      return $has(permissions.Registers.Delete) && this.mode === 'register';
    },
    hasMore() {
      const pagination = this.GET_AUDIT_PAGINATION;
      return this.logs.length < this.total && pagination.page < pagination.pageCount;
    },
    headerSubtitle() {
      if (this.mode === 'row' && this.selectedRow) {
        const rowId = this.selectedRow.Id || this.selectedRow.id;
        return `Row: ${rowId?.slice(0, 8) || 'Selected'}...`;
      }
      return this.registerName || 'Register';
    },
    activeFiltersCount() {
      let count = 0;
      if (this.localFilters.actionType) count++;
      if (this.localFilters.fromDate) count++;
      if (this.localFilters.toDate) count++;
      return count;
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        // Sync local filters with store
        const storeFilters = this.GET_AUDIT_FILTERS;
        this.localFilters = {
          actionType: storeFilters.actionType,
          fromDate: storeFilters.fromDate,
          toDate: storeFilters.toDate
        };
      }
    }
  },
  methods: {
    ...mapActions('REGISTERS', [
      'CLOSE_AUDIT_SIDEBAR',
      'FETCH_REGISTER_AUDIT_LOGS',
      'FETCH_ROW_AUDIT_LOGS',
      'UPDATE_AUDIT_FILTERS',
      'CLEAR_AUDIT_LOGS'
    ]),
    handleDrawerInput(val) {
      if (!val) {
        this.closeSidebar();
      }
    },
    closeSidebar() {
      this.CLOSE_AUDIT_SIDEBAR();
    },
    onModeChange(newMode) {
      if (newMode === 'row' && this.selectedRow) {
        const rowId = this.selectedRow.Id || this.selectedRow.id;
        this.FETCH_ROW_AUDIT_LOGS(rowId);
      } else if (newMode === 'register' && this.registerId) {
        this.FETCH_REGISTER_AUDIT_LOGS(this.registerId);
      }
      // Update store mode
      this.$store.commit('REGISTERS/SET_AUDIT_SIDEBAR_MODE', newMode);
    },
    applyFilters() {
      const filters = {
        ...this.localFilters,
        page: 1 // Reset to first page when filtering
      };
      this.UPDATE_AUDIT_FILTERS(filters);
    },
    clearFilters() {
      this.localFilters = {
        actionType: null,
        fromDate: null,
        toDate: null
      };
      this.applyFilters();
    },
    refresh() {
      if (this.mode === 'row' && this.selectedRow) {
        const rowId = this.selectedRow.Id || this.selectedRow.id;
        this.FETCH_ROW_AUDIT_LOGS(rowId);
      } else if (this.registerId) {
        this.FETCH_REGISTER_AUDIT_LOGS(this.registerId);
      }
    },
    loadMore() {
      const currentFilters = this.GET_AUDIT_FILTERS;
      this.UPDATE_AUDIT_FILTERS({
        page: currentFilters.page + 1
      });
    },
    async confirmClearLogs() {
      if (!this.registerId) return;

      this.clearing = true;
      try {
        const olderThan = this.clearOlderOnly
          ? new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
          : null;

        const result = await this.CLEAR_AUDIT_LOGS({
          registerId: this.registerId,
          olderThan
        });

        this.showClearDialog = false;

        // Show appropriate feedback based on result
        if (result && result.deletedCount > 0) {
          this.$dialog?.notify?.success?.(`Successfully cleared ${result.deletedCount} audit log(s)`);
        } else if (this.clearOlderOnly && this.total > 0) {
          // No logs were cleared but there ARE logs - they're just not old enough
          this.$dialog?.notify?.info?.('No logs older than 30 days found. Uncheck the filter to clear all logs.');
        } else {
          this.$dialog?.notify?.info?.('No audit logs to clear');
        }

        this.$emit('logs-cleared');
      } catch (error) {
        error('Failed to clear audit logs:', error);
        this.$dialog?.notify?.error?.('Failed to clear audit logs');
      } finally {
        this.clearing = false;
      }
    }
  }
};
</script>

<style scoped>
/* ============================================
   REGISTER AUDIT SIDEBAR
   Clean, professional timeline interface
   Push-style sidebar (no overlay)
   ============================================ */

.audit-sidebar-wrapper {
  flex-shrink: 0;
  width: 420px;
  height: 100%;
  border-left: 1px solid var(--ds-border-default);
  animation: slideIn 0.25s ease-out;
}

@keyframes slideIn {
  from {
    width: 0;
    opacity: 0;
  }
  to {
    width: 420px;
    opacity: 1;
  }
}

/* Overlay variant — floats over sibling content instead of pushing it.
   Anchors to the nearest positioned ancestor (the host view must be
   `position: relative`). z-index clears the viewer internals (max 10) while
   staying well below Vuetify's teleported dialogs/menus (200+). Uses a
   translateX entrance to avoid the content reflow a width animation would
   cause on an absolutely-positioned element. */
.audit-sidebar-wrapper--overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  height: 100%;
  z-index: 20;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.18);
  animation: auditOverlaySlideIn 0.25s ease-out;
}

@keyframes auditOverlaySlideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.audit-sidebar {
  background: var(--v-bg2-base);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* Header */
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--ds-space-md) var(--ds-space-md);
  background: var(--v-primary-base);
  flex-shrink: 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: var(--ds-space-sm);
}

.header-icon {
  color: rgba(255, 255, 255, 0.9) !important;
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.header-title {
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  line-height: 1.2;
}

.header-subtitle {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--ds-space-sm);
}

.header-stats {
  display: flex;
  align-items: baseline;
  gap: 4px;
  padding: 2px 8px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: var(--ds-radius-sm);
}

.stat-value {
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
}

.stat-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
}

.close-btn {
  color: rgba(255, 255, 255, 0.8) !important;
}

.close-btn:hover {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.15) !important;
}

/* Mode Toggle */
.mode-toggle-container {
  padding: var(--ds-space-md);
  border-bottom: 1px solid var(--ds-border-default);
  flex-shrink: 0;
}

.mode-toggle {
  width: 100%;
  border-radius: var(--ds-radius-md) !important;
  background: var(--v-bg3-base) !important;
}

.mode-btn {
  flex: 1;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 500 !important;
  height: 36px !important;
}

.mode-btn.v-btn--active {
  background: var(--v-primary-base) !important;
  color: white !important;
}

/* Filters Section */
.filters-section {
  border-bottom: 1px solid var(--ds-border-default);
  flex-shrink: 0;
}

.filters-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--ds-space-sm) var(--ds-space-md);
  cursor: pointer;
  transition: background 0.15s ease;
}

.filters-header:hover {
  background: var(--v-bg3-base);
}

.filters-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--ds-font-body-sm);
  font-weight: 500;
  color: var(--v-text-base);
}

.filter-count {
  height: 18px !important;
  font-size: 10px !important;
}

.filters-header .v-icon {
  color: var(--v-text_secondary-base);
  transition: transform 0.2s ease;
}

.filters-header .v-icon.rotated {
  transform: rotate(180deg);
}

.filters-body {
  padding: 0 var(--ds-space-md) var(--ds-space-md);
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-sm);
}

.filter-input {
  font-size: var(--ds-font-body-sm);
}

.date-range {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--ds-space-sm);
}

.date-input {
  font-size: var(--ds-font-body-sm);
}

.clear-filters-btn {
  align-self: flex-start;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

/* Slide Transition */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.slide-enter-to,
.slide-leave {
  max-height: 200px;
}

/* Toolbar */
.sidebar-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--ds-space-sm) var(--ds-space-md);
  border-bottom: 1px solid var(--ds-border-default);
  background: var(--v-bg3-base);
  flex-shrink: 0;
}

.results-info {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.results-count {
  font-size: var(--ds-font-subtitle);
  font-weight: 700;
  color: var(--v-text-base);
}

.results-label {
  font-size: var(--ds-font-caption);
  color: var(--v-text_secondary-base);
}

.toolbar-actions {
  display: flex;
  gap: 4px;
}

.refresh-btn,
.clear-logs-btn {
  color: var(--v-text_secondary-base) !important;
}

.refresh-btn:hover {
  color: var(--v-primary-base) !important;
}

.clear-logs-btn:hover {
  color: var(--v-error-base) !important;
}

/* Content Area */
.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: var(--ds-space-md);
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--ds-space-2xl);
  gap: var(--ds-space-md);
  color: var(--v-text_secondary-base);
  font-size: var(--ds-font-body-sm);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--ds-space-2xl);
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  border-radius: var(--ds-radius-full);
  background: var(--v-bg3-base);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--ds-space-md);
}

.empty-title {
  font-size: var(--ds-font-subtitle);
  font-weight: 600;
  color: var(--v-text-base);
  margin: 0 0 var(--ds-space-xs) 0;
}

.empty-subtitle {
  font-size: var(--ds-font-body-sm);
  color: var(--v-text_secondary-base);
  margin: 0;
  max-width: 280px;
}

/* Audit List */
.audit-list {
  display: flex;
  flex-direction: column;
}

.load-more {
  display: flex;
  justify-content: center;
  padding: var(--ds-space-md) 0;
}

.load-more .v-btn {
  text-transform: none;
  letter-spacing: 0;
}

/* Clear Dialog */
.clear-dialog {
  border-radius: var(--ds-radius-lg) !important;
}

.clear-dialog-title {
  font-size: var(--ds-font-subtitle) !important;
  font-weight: 600 !important;
}

.clear-dialog-text {
  font-size: var(--ds-font-body) !important;
  color: var(--v-text-base) !important;
}

.warning-text {
  color: var(--v-error-base);
  font-weight: 500;
}
</style>
